// Tier 1 + Tier 2 Lessons Hub.
// Tier 1 (BEGINNER FOUNDATION) — 27 lessons across modules 1.1-1.6.
// Tier 2 (INTERMEDIATE — SuPER System) — 25 lessons across modules 2.1-2.5,
//        locked until Tier 1 graduation; 75% quiz pass threshold; ends with
//        a self-reported milestone gate (30 demo trades) → SuPER Certified cert.
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { TIER1_LESSONS } from "./data/lessons-tier1.js";
import { TIER2_LESSONS } from "./data/lessons-tier2.js";
import { TIER3_LESSONS } from "./data/lessons-tier3.js";
import { TIER4_LESSONS } from "./data/lessons-tier4.js";
import { TIER5_LESSONS } from "./data/lessons-tier5.js";
import { THEME, awardXP, flexxState } from "./state";
import Portal from "./Portal";
import {
  TIER2_PHASES, TIER2_PASS_THRESHOLD, TIER2_LESSON_XP,
  TIER2_QUIZ_PASS_BONUS, TIER2_TIER_BONUS,
  Tier2Header, Tier2GatePlaceholder,
  Tier2MilestoneGate, Tier2CertificateModal,
} from "./LessonsHub.Tier2";
import {
  TIER3_PHASES, TIER3_PASS_THRESHOLD, TIER3_LESSON_XP,
  TIER3_QUIZ_PASS_BONUS, TIER3_TIER_BONUS, TIER3_EMERALD,
  Tier3Header, Tier3GatePlaceholder,
  Tier3MilestoneGate, Tier3CertificateModal,
} from "./LessonsHub.Tier3";
import {
  TIER4_PHASES, TIER4_PASS_THRESHOLD, TIER4_LESSON_XP,
  TIER4_QUIZ_PASS_BONUS, TIER4_MODULE_BONUS, TIER4_TIER_BONUS, TIER4_BLUE,
  Tier4Header, Tier4GatePlaceholder, Tier5PreviewLocked,
  Tier4MilestoneGate, Tier4CertificateModal,
} from "./LessonsHub.Tier4";
import {
  TIER5_PHASES, TIER5_PASS_THRESHOLD, TIER5_LESSON_XP,
  TIER5_QUIZ_PASS_BONUS, TIER5_MODULE_BONUS, TIER5_TIER_BONUS, TIER5_PURPLE,
  Tier5Header, Tier5GatePlaceholder,
  Tier5MilestoneGate, Tier5CertificateModal,
} from "./LessonsHub.Tier5";

// ── Module/phase metadata ────────────────────────────────────────
// id "1.X.Y" → module_id "b1-X" + phase_label per spec.
const PHASE_LABELS = {
  "1.1": "Financial Markets 101",
  "1.2": "Asset Fundamentals",
  "1.3": "Markets & Sessions",
  "1.4": "Risk Management",
  "1.5": "Charts & Structure",
  "1.6": "Psychology & Routine",
  "2.1": "Introduction to SuPER",
  "2.2": "Order Blocks & Fair Value Gaps",
  "2.3": "The u: Understand Step",
  "2.4": "P: Plan + E: Execute",
  "2.5": "R: Review + Graduation",
  "3.1": "AI Revolution + SageMaster Setup",
  "3.2": "Magic Terminal + Helix Analytics",
  "3.3": "AI Grid, DCA + Omni Bots",
  "3.4": "AI Strategy Pack + Backtest",
  "3.5": "AI Trader Graduation",
  "4.1": "MT5 Mastery",
  "4.2": "Building the SuPER EA",
  "4.3": "VPS + Webhook Pipeline",
  "4.4": "Optimization + Prop Firm",
  "4.5": "Algo Trader Graduation",
  "5.1": "Quant Foundations & Python Setup",
  "5.2": "Probability, Statistics & Market Math",
  "5.3": "Statistical Edge & Backtesting",
  "5.4": "Python for Trading: Applied Quant",
  "5.5": "Machine Learning in Trading",
  "5.6": "Quant Systems & Live Deployment",
};
const MODULE_LESSON_COUNT = {
  "1.1": 4, "1.2": 5, "1.3": 4, "1.4": 5, "1.5": 5, "1.6": 5,
  "2.1": 5, "2.2": 5, "2.3": 5, "2.4": 5, "2.5": 5,
  "3.1": 5, "3.2": 5, "3.3": 5, "3.4": 5, "3.5": 5,
  "4.1": 5, "4.2": 5, "4.3": 5, "4.4": 5, "4.5": 5,
  "5.1": 5, "5.2": 5, "5.3": 5, "5.4": 5, "5.5": 5, "5.6": 5,
};
const MODULE_BONUS = {
  "1.1": 0, "1.2": 0, "1.3": 0, "1.4": 0, "1.5": 100, "1.6": 150,
  "2.1": 25, "2.2": 25, "2.3": 25, "2.4": 25, "2.5": 25,
  "3.1": 30, "3.2": 30, "3.3": 30, "3.4": 30, "3.5": 30,
  "4.1": TIER4_MODULE_BONUS, "4.2": TIER4_MODULE_BONUS, "4.3": TIER4_MODULE_BONUS, "4.4": TIER4_MODULE_BONUS, "4.5": TIER4_MODULE_BONUS,
  "5.1": TIER5_MODULE_BONUS, "5.2": TIER5_MODULE_BONUS, "5.3": TIER5_MODULE_BONUS, "5.4": TIER5_MODULE_BONUS, "5.5": TIER5_MODULE_BONUS, "5.6": TIER5_MODULE_BONUS,
};
const TIER1_PHASES = ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6"];
const TIER1_PASS_THRESHOLD = 0.7;
const tierForId = (id) => {
  const s = id || "";
  if (s.startsWith("5.")) return 5;
  if (s.startsWith("4.")) return 4;
  if (s.startsWith("3.")) return 3;
  if (s.startsWith("2.")) return 2;
  return 1;
};
const passThresholdForLesson = (l) => {
  const t = tierForId(l.id);
  if (t === 5) return TIER5_PASS_THRESHOLD;
  if (t === 4) return TIER4_PASS_THRESHOLD;
  if (t === 2) return TIER2_PASS_THRESHOLD;
  return TIER1_PASS_THRESHOLD;
};

// Normalise both schemas (nested axel_script vs flat axel_hook/core_teaching/…)
function normalise(raw) {
  const id = raw.id;
  const parts = (id || "").split(".");
  const phase = parts.slice(0, 2).join(".");  // "1.5.3" → "1.5"
  const tier = phase.startsWith("5.") ? 5 : phase.startsWith("4.") ? 4 : phase.startsWith("3.") ? 3 : phase.startsWith("2.") ? 2 : 1;
  const moduleId = raw.module_id || `b${tier}-${parts[1] || "0"}`;
  const script = raw.axel_script || {};
  return {
    id, title: raw.title, tag: raw.tag, duration: raw.duration,
    phase: raw.phase || phase, tier,
    phase_label: raw.phase_label || PHASE_LABELS[phase] || phase,
    module_id: moduleId,
    axel_script: {
      hook: script.hook ?? raw.axel_hook ?? raw.hook,
      core_teaching: script.core_teaching ?? raw.core_teaching,
      key_rules: script.key_rules ?? raw.key_rules,
      practice_assignment: script.practice_assignment ?? raw.practice_assignment,
      closing: script.closing ?? raw.axel_closing ?? raw.closing,
    },
    quiz: raw.quiz || [],
    common_mistakes: raw.common_mistakes || [],
  };
}

const LESSONS = [
  ...TIER1_LESSONS, ...TIER2_LESSONS, ...TIER3_LESSONS, ...TIER4_LESSONS, ...TIER5_LESSONS,
].map(normalise).sort((a, b) => (a.id || "").localeCompare(b.id || "", undefined, { numeric: true }));

// Lesson-completion state lives in flexxState (so it cloud-syncs across devices).
const readCompletion = () => flexxState.lessonCompletion();
const markCompleted = (id, score) => flexxState.markLessonPassed(id, score);
const readBonusAwarded = () => flexxState.moduleBonusAwarded();
const markBonusAwarded = (moduleId) => flexxState.markModuleBonus(moduleId);

// Fire-and-forget: log every quiz attempt to backend for analytics + sync ledger.
async function postQuizAttempt(payload) {
  try {
    const token = localStorage.getItem("flexx_access_token");
    if (!token) return; // anonymous — local only
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    await axios.post(`${API}/flexx/quiz-attempt`, payload, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
  } catch (_) { /* silent — local state still authoritative */ }
}

// Issue a backend-signed Tier 2/3/4 certificate and persist locally for the cert modal + PDF.
async function issueTierCertificate(tier) {
  try {
    const token = localStorage.getItem("flexx_access_token");
    if (!token) return null; // anonymous — skip backend cert
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    // Push latest state so backend sees tier{N}Grad before issuing
    try { await axios.put(`${API}/flexx/state`, { data: flexxState.snapshot() },
      { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }); } catch (_) {}
    const { data } = await axios.post(`${API}/flexx/certificate/issue-tier`, { tier },
      { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
    if (data?.cert_id) flexxState.storeTierCert(tier, data);
    return data || null;
  } catch (_) { return null; }
}

export default function LessonsHub() {
  const [completion, setCompletion] = useState(() => readCompletion());
  const [activeLesson, setActiveLesson] = useState(null);
  const [quizLesson, setQuizLesson] = useState(null);
  const [graduated, setGraduated] = useState(false);  // Tier 1 grad modal
  const [showT2Gate, setShowT2Gate] = useState(false);
  const [t2Certified, setT2Certified] = useState(false); // Tier 2 cert modal
  const [showT3Gate, setShowT3Gate] = useState(false);
  const [t3Certified, setT3Certified] = useState(false); // Tier 3 cert modal
  const [showT4Gate, setShowT4Gate] = useState(false);
  const [t4Certified, setT4Certified] = useState(false); // Tier 4 cert modal
  const [showT5Gate, setShowT5Gate] = useState(false);
  const [t5Certified, setT5Certified] = useState(false); // Tier 5 cert modal (FULL CURRICULUM)
  const tier1Done = !!flexxState.tier1Graduate();
  const tier2Done = !!flexxState.tier2Graduate();
  const tier3Done = !!flexxState.tier3Graduate();
  const tier4Done = !!flexxState.tier4Graduate();
  const tier5Done = !!flexxState.tier5Graduate();

  const lessonsT1 = useMemo(() => LESSONS.filter((l) => l.tier === 1), []);
  const lessonsT2 = useMemo(() => LESSONS.filter((l) => l.tier === 2), []);
  const lessonsT3 = useMemo(() => LESSONS.filter((l) => l.tier === 3), []);
  const lessonsT4 = useMemo(() => LESSONS.filter((l) => l.tier === 4), []);
  const lessonsT5 = useMemo(() => LESSONS.filter((l) => l.tier === 5), []);
  const passedCountT1 = useMemo(() =>
    lessonsT1.filter((l) => completion[l.id]?.passed).length, [completion, lessonsT1]);
  const passedCountT2 = useMemo(() =>
    lessonsT2.filter((l) => completion[l.id]?.passed).length, [completion, lessonsT2]);
  const passedCountT3 = useMemo(() =>
    lessonsT3.filter((l) => completion[l.id]?.passed).length, [completion, lessonsT3]);
  const passedCountT4 = useMemo(() =>
    lessonsT4.filter((l) => completion[l.id]?.passed).length, [completion, lessonsT4]);
  const passedCountT5 = useMemo(() =>
    lessonsT5.filter((l) => completion[l.id]?.passed).length, [completion, lessonsT5]);

  const phaseGroupsAll = useMemo(() => {
    const map = {};
    LESSONS.forEach((l) => {
      const k = l.phase || l.phase_label;
      (map[k] = map[k] || { label: l.phase_label || k, items: [], tier: l.tier }).items.push(l);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, []);
  const phaseGroupsT1 = useMemo(() => phaseGroupsAll.filter(([, g]) => g.tier === 1), [phaseGroupsAll]);
  const phaseGroupsT2 = useMemo(() => phaseGroupsAll.filter(([, g]) => g.tier === 2), [phaseGroupsAll]);
  const phaseGroupsT3 = useMemo(() => phaseGroupsAll.filter(([, g]) => g.tier === 3), [phaseGroupsAll]);
  const phaseGroupsT4 = useMemo(() => phaseGroupsAll.filter(([, g]) => g.tier === 4), [phaseGroupsAll]);
  const phaseGroupsT5 = useMemo(() => phaseGroupsAll.filter(([, g]) => g.tier === 5), [phaseGroupsAll]);

  const moduleProgress = useMemo(() => {
    const out = {};
    Object.keys(MODULE_LESSON_COUNT).forEach((p) => {
      const total = LESSONS.filter((l) => l.phase === p).length || MODULE_LESSON_COUNT[p];
      const done = LESSONS.filter((l) => l.phase === p && completion[l.id]?.passed).length;
      out[p] = { total, done };
    });
    return out;
  }, [completion]);

  // Module locking — first module of each tier is open (Tier 2 gated separately
  // by tier1Done); subsequent modules unlock at ≥50% of the previous module.
  const lockedModules = useMemo(() => {
    const locked = {};
    [TIER1_PHASES, TIER2_PHASES, TIER3_PHASES, TIER4_PHASES].forEach((tierPhases) => {
      tierPhases.forEach((p, i) => {
        if (i === 0) { locked[p] = false; return; }
        const prev = tierPhases[i - 1];
        const mp = moduleProgress[prev] || { total: 1, done: 0 };
        locked[p] = (mp.done / mp.total) < 0.5;
      });
    });
    return locked;
  }, [moduleProgress]);

  const onQuizComplete = (lesson, score, meta = {}) => {
    const tier = lesson.tier;
    const passThreshold = passThresholdForLesson(lesson);
    const passed = score >= passThreshold;

    // Log EVERY attempt to backend (passed or failed) — fire-and-forget.
    postQuizAttempt({
      lesson_id: lesson.id,
      score,
      passed,
      tier: String(tier),
      module: lesson.phase,
      correct: meta.correct,
      total: meta.total,
      duration_sec: meta.duration_sec,
    });

    if (!passed) return;
    const wasPassed = !!completion[lesson.id]?.passed;
    markCompleted(lesson.id, score);
    const next = readCompletion();
    setCompletion(next);

    // Base XP — Tier 1: 50 first pass + 25 perfect; Tier 2: 35 + 5 quiz pass + 5 perfect;
    // Tier 3: 50 first pass + 8 quiz pass + 8 perfect.
    if (!wasPassed) {
      if (tier === 5) {
        awardXP(TIER5_LESSON_XP, `Lesson ${lesson.id} passed`);
        awardXP(TIER5_QUIZ_PASS_BONUS, `Quiz pass · ${lesson.id}`);
      } else if (tier === 4) {
        awardXP(TIER4_LESSON_XP, `Lesson ${lesson.id} passed`);
        awardXP(TIER4_QUIZ_PASS_BONUS, `Quiz pass · ${lesson.id}`);
      } else if (tier === 3) {
        awardXP(TIER3_LESSON_XP, `Lesson ${lesson.id} passed`);
        awardXP(TIER3_QUIZ_PASS_BONUS, `Quiz pass · ${lesson.id}`);
      } else if (tier === 2) {
        awardXP(TIER2_LESSON_XP, `Lesson ${lesson.id} passed`);
        awardXP(TIER2_QUIZ_PASS_BONUS, `Quiz pass · ${lesson.id}`);
      } else {
        awardXP(50, `Lesson ${lesson.id} passed`);
      }
    }
    if (score >= 0.999) awardXP(tier >= 4 ? 12 : tier === 3 ? 8 : tier === 2 ? 5 : 25, `Perfect score on ${lesson.id}`);

    // Module-completion bonus
    const phase = lesson.phase;
    const expected = LESSONS.filter((l) => l.phase === phase).length || MODULE_LESSON_COUNT[phase];
    const passedInModule = LESSONS.filter((l) => l.phase === phase && next[l.id]?.passed).length;
    const bonusAwarded = readBonusAwarded();
    if (expected > 0 && passedInModule >= expected && !bonusAwarded[phase] && MODULE_BONUS[phase] > 0) {
      awardXP(MODULE_BONUS[phase], `Module ${phase} complete bonus`);
      markBonusAwarded(phase);
    }

    // Tier 1 graduation — all 6 Tier 1 modules done
    const tier1Total = TIER1_PHASES.reduce((a, p) => a + MODULE_LESSON_COUNT[p], 0);
    const tier1DoneCount = lessonsT1.filter((l) => next[l.id]?.passed).length;
    if (tier1DoneCount >= tier1Total && !flexxState.tier1Graduate()) {
      flexxState.markTier1Graduate();
      flexxState.addAchievement("tier-1-graduate");
      setGraduated(true);
    }

    // Tier 2 — when all 25 lessons passed, surface the milestone gate
    // (the actual cert is awarded only after the user confirms their 30 demo trades).
    const tier2Total = TIER2_PHASES.reduce((a, p) => a + MODULE_LESSON_COUNT[p], 0);
    const tier2DoneCount = lessonsT2.filter((l) => next[l.id]?.passed).length;
    if (tier2DoneCount >= tier2Total && !flexxState.tier2GateConfirmed() && !flexxState.tier2Graduate()) {
      setShowT2Gate(true);
    }

    // Tier 3 — when all 25 Tier 3 lessons passed, surface the AI TRADER milestone gate.
    const tier3Total = TIER3_PHASES.reduce((a, p) => a + MODULE_LESSON_COUNT[p], 0);
    const tier3DoneCount = lessonsT3.filter((l) => next[l.id]?.passed).length;
    if (tier3DoneCount >= tier3Total && !flexxState.tier3GateConfirmed() && !flexxState.tier3Graduate()) {
      setShowT3Gate(true);
    }

    // Tier 4 — when all 25 Tier 4 lessons passed, surface the ALGO TRADER milestone gate.
    const tier4Total = TIER4_PHASES.reduce((a, p) => a + MODULE_LESSON_COUNT[p], 0);
    const tier4DoneCount = lessonsT4.filter((l) => next[l.id]?.passed).length;
    if (tier4DoneCount >= tier4Total && !flexxState.tier4GateConfirmed() && !flexxState.tier4Graduate()) {
      setShowT4Gate(true);
    }

    // Tier 5 — when all 30 Tier 5 lessons passed AND quizzes ≥75%, surface the FULL CURRICULUM gate.
    const tier5Total = TIER5_PHASES.reduce((a, p) => a + MODULE_LESSON_COUNT[p], 0);
    const tier5DoneCount = lessonsT5.filter((l) => next[l.id]?.passed).length;
    if (tier5DoneCount >= tier5Total && !flexxState.tier5GateConfirmed() && !flexxState.tier5Graduate()) {
      setShowT5Gate(true);
    }
  };

  // Called from the milestone gate when student self-reports 30 demo trades done.
  const onConfirmT2Gate = () => {
    if (flexxState.tier2Graduate()) return;
    flexxState.markTier2GateConfirmed({ demoTradesConfirmed: 30 });
    flexxState.markTier2Graduate();
    flexxState.addAchievement("tier-2-graduate");
    awardXP(TIER2_TIER_BONUS, "Tier 2 (SuPER Certified) complete");
    issueTierCertificate(2);  // fire-and-forget backend signing
    setShowT2Gate(false);
    setT2Certified(true);
  };

  const onConfirmT3Gate = () => {
    if (flexxState.tier3Graduate()) return;
    flexxState.markTier3GateConfirmed({ aiRoutineConfirmed: 30 });
    flexxState.markTier3Graduate();
    flexxState.addAchievement("tier-3-graduate");
    awardXP(TIER3_TIER_BONUS, "Tier 3 (AI TRADER) complete");
    issueTierCertificate(3);
    setShowT3Gate(false);
    setT3Certified(true);
  };

  const onConfirmT4Gate = () => {
    if (flexxState.tier4Graduate()) return;
    flexxState.markTier4GateConfirmed({ eaAutonomousDays: 30 });
    flexxState.markTier4Graduate();
    flexxState.addAchievement("tier-4-graduate");
    awardXP(TIER4_TIER_BONUS, "Tier 4 (ALGO TRADER) complete");
    issueTierCertificate(4);
    setShowT4Gate(false);
    setT4Certified(true);
  };

  // Tier 5 = FULL CURRICULUM graduation. Awards the largest XP bonus and
  // issues the "QUANT CERTIFIED" final diploma covering all 5 tiers.
  const onConfirmT5Gate = () => {
    if (flexxState.tier5Graduate()) return;
    flexxState.markTier5GateConfirmed({ liveDeployDays: 30 });
    flexxState.markTier5Graduate();
    flexxState.addAchievement("tier-5-graduate");
    flexxState.addAchievement("flexx-full-curriculum");
    awardXP(TIER5_TIER_BONUS, "Tier 5 (QUANT CERTIFIED) — Full Curriculum Complete");
    issueTierCertificate(5);
    setShowT5Gate(false);
    setT5Certified(true);
  };

  return (
    <section data-testid="lessons-hub" style={{ marginTop: 24 }}>
      {/* ── TIER 1 ───────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ fontSize: 11, letterSpacing: 2.5, color: THEME.gold, fontFamily: "monospace" }}>TIER 1 · LESSONS</div>
        <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>
          BEGINNER FOUNDATION
        </div>
        <div data-testid="lessons-progress" style={{ marginLeft: "auto", color: THEME.mute, fontFamily: "monospace", fontSize: 12 }}>
          {passedCountT1} / {lessonsT1.length} passed
        </div>
      </div>

      {phaseGroupsT1.map(([phase, group]) =>
        <ModuleRow key={phase} phase={phase} group={group} completion={completion}
          moduleProgress={moduleProgress} lockedModules={lockedModules}
          tierPhases={TIER1_PHASES} onOpenLesson={setActiveLesson} />)}

      {/* ── TIER 2 ───────────────────────────────────────────── */}
      <Tier2Header passed={passedCountT2} total={lessonsT2.length} unlocked={tier1Done} certified={tier2Done} />
      {tier1Done ? (
        phaseGroupsT2.map(([phase, group]) =>
          <ModuleRow key={phase} phase={phase} group={group} completion={completion}
            moduleProgress={moduleProgress} lockedModules={lockedModules}
            tierPhases={TIER2_PHASES} onOpenLesson={setActiveLesson} />)
      ) : (
        <Tier2GatePlaceholder />
      )}
      {tier1Done && passedCountT2 >= lessonsT2.length && !tier2Done && (
        <button data-testid="tier2-gate-open-btn" onClick={() => setShowT2Gate(true)}
          style={{ ...primaryBtn, width: "100%", marginTop: 16, padding: "14px 18px", fontSize: 13 }}>
          REVIEW MILESTONE CHECKLIST →
        </button>
      )}

      {/* ── TIER 3 ───────────────────────────────────────────── */}
      <Tier3Header passed={passedCountT3} total={lessonsT3.length} unlocked={tier2Done} certified={tier3Done} />
      {tier2Done ? (
        phaseGroupsT3.map(([phase, group]) =>
          <ModuleRow key={phase} phase={phase} group={group} completion={completion}
            moduleProgress={moduleProgress} lockedModules={lockedModules}
            tierPhases={TIER3_PHASES} onOpenLesson={setActiveLesson} />)
      ) : (
        <Tier3GatePlaceholder />
      )}
      {tier2Done && passedCountT3 >= lessonsT3.length && !tier3Done && (
        <button data-testid="tier3-gate-open-btn" onClick={() => setShowT3Gate(true)}
          style={{
            ...primaryBtn, width: "100%", marginTop: 16, padding: "14px 18px", fontSize: 13,
            background: TIER3_EMERALD, borderColor: TIER3_EMERALD, color: "#0a0a0a",
          }}>
          REVIEW AI TRADER MILESTONE CHECKLIST →
        </button>
      )}

      {/* ── TIER 4 ───────────────────────────────────────────── */}
      <Tier4Header passed={passedCountT4} total={lessonsT4.length} unlocked={tier3Done} certified={tier4Done} />
      {tier3Done ? (
        phaseGroupsT4.map(([phase, group]) =>
          <ModuleRow key={phase} phase={phase} group={group} completion={completion}
            moduleProgress={moduleProgress} lockedModules={lockedModules}
            tierPhases={TIER4_PHASES} onOpenLesson={setActiveLesson} />)
      ) : (
        <Tier4GatePlaceholder />
      )}
      {tier3Done && passedCountT4 >= lessonsT4.length && !tier4Done && (
        <button data-testid="tier4-gate-open-btn" onClick={() => setShowT4Gate(true)}
          style={{
            ...primaryBtn, width: "100%", marginTop: 16, padding: "14px 18px", fontSize: 13,
            background: TIER4_BLUE, borderColor: TIER4_BLUE, color: "#0a0a0a",
          }}>
          REVIEW ALGO TRADER MILESTONE CHECKLIST →
        </button>
      )}

      {/* ── TIER 5 — QUANTITATIVE TRADING (final tier) ─────────── */}
      <Tier5Header passed={passedCountT5} total={lessonsT5.length} unlocked={tier4Done} certified={tier5Done} />
      {tier4Done ? (
        phaseGroupsT5.map(([phase, group]) =>
          <ModuleRow key={phase} phase={phase} group={group} completion={completion}
            moduleProgress={moduleProgress} lockedModules={lockedModules}
            tierPhases={TIER5_PHASES} onOpenLesson={setActiveLesson} />)
      ) : (
        <Tier5GatePlaceholder />
      )}
      {tier4Done && passedCountT5 >= lessonsT5.length && !tier5Done && (
        <button data-testid="tier5-gate-open-btn" onClick={() => setShowT5Gate(true)}
          style={{
            ...primaryBtn, width: "100%", marginTop: 16, padding: "14px 18px", fontSize: 13,
            background: TIER5_PURPLE, borderColor: TIER5_PURPLE, color: "#0a0a0a",
            boxShadow: `0 0 24px rgba(168,85,247,0.35)`,
          }}>
          REVIEW QUANT TRADER MILESTONE CHECKLIST →
        </button>
      )}

      {/* ── Modals ───────────────────────────────────────────── */}
      {activeLesson && (
        <LessonViewer lesson={activeLesson}
          onClose={() => setActiveLesson(null)}
          onQuiz={() => { setQuizLesson(activeLesson); setActiveLesson(null); }} />
      )}
      {quizLesson && (
        <QuizModal lesson={quizLesson}
          onClose={() => setQuizLesson(null)}
          onComplete={(score, meta) => onQuizComplete(quizLesson, score, meta)} />
      )}
      {graduated && <GraduationModal onClose={() => setGraduated(false)} />}
      {showT2Gate && (
        <Tier2MilestoneGate
          tier2Lessons={lessonsT2}
          completion={completion}
          onConfirm={onConfirmT2Gate}
          onClose={() => setShowT2Gate(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
      )}
      {t2Certified && (
        <Tier2CertificateModal
          onClose={() => setT2Certified(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ConfettiComp={Confetti} />
      )}
      {showT3Gate && (
        <Tier3MilestoneGate
          tier3Lessons={lessonsT3}
          completion={completion}
          onConfirm={onConfirmT3Gate}
          onClose={() => setShowT3Gate(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
      )}
      {t3Certified && (
        <Tier3CertificateModal
          onClose={() => setT3Certified(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ConfettiComp={Confetti} />
      )}
      {showT4Gate && (
        <Tier4MilestoneGate
          tier4Lessons={lessonsT4}
          completion={completion}
          onConfirm={onConfirmT4Gate}
          onClose={() => setShowT4Gate(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
      )}
      {t4Certified && (
        <Tier4CertificateModal
          onClose={() => setT4Certified(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ConfettiComp={Confetti} />
      )}
      {showT5Gate && (
        <Tier5MilestoneGate
          tier5Lessons={lessonsT5}
          completion={completion}
          onConfirm={onConfirmT5Gate}
          onClose={() => setShowT5Gate(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
      )}
      {t5Certified && (
        <Tier5CertificateModal
          onClose={() => setT5Certified(false)}
          modalBg={modalBg} modalCard={modalCard}
          primaryBtn={primaryBtn} ConfettiComp={Confetti} />
      )}
    </section>
  );
}

// Re-usable module-row renderer (used by both tiers).
function ModuleRow({ phase, group, completion, moduleProgress, lockedModules, tierPhases, onOpenLesson }) {
  const mp = moduleProgress[phase] || { total: group.items.length, done: 0 };
  const locked = lockedModules[phase];
  const prevPhase = tierPhases[tierPhases.indexOf(phase) - 1];
  const prevMp = prevPhase ? moduleProgress[prevPhase] : null;
  const needToUnlock = prevMp ? Math.max(0, Math.ceil(prevMp.total * 0.5) - prevMp.done) : 0;
  return (
    <div data-testid={`module-row-${phase}`} data-locked={locked ? "true" : "false"}
      style={{ marginBottom: 18, opacity: locked ? 0.55 : 1, transition: "opacity 0.5s ease" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ color: locked ? THEME.mute : THEME.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 2 }}>
          {locked && "🔒 "}MODULE {phase} · {(group.label || "").toUpperCase()}
        </div>
        <div data-testid={`module-progress-${phase}`} style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 1 }}>
          {locked
            ? `Pass ${needToUnlock} more in Module ${prevPhase} to unlock`
            : `${mp.done}/${mp.total}${MODULE_BONUS[phase] ? ` · +${MODULE_BONUS[phase]} XP bonus` : ""}`}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {group.items.map((l) => (
          <LessonCard key={l.id} lesson={l} done={!!completion[l.id]?.passed} locked={locked}
            onOpen={() => { if (!locked) onOpenLesson(l); }} />
        ))}
      </div>
    </div>
  );
}



function LessonCard({ lesson, done, locked, onOpen }) {
  return (
    <button data-testid={`lesson-card-${lesson.id}`} onClick={onOpen} disabled={locked}
      aria-disabled={locked}
      style={{
        all: "unset", cursor: locked ? "not-allowed" : "pointer", display: "block",
        background: done ? `${THEME.gold}10` : "#0d1218",
        border: `1px solid ${done ? THEME.gold : THEME.border}`,
        borderLeft: `3px solid ${done ? "#22C55E" : (locked ? THEME.border : THEME.gold)}`,
        borderRadius: 10, padding: 14,
        filter: locked ? "grayscale(0.6)" : "none",
        transition: "all 0.25s",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: "monospace", color: locked ? THEME.mute : THEME.gold, fontSize: 10, letterSpacing: 1.5 }}>
          {locked ? "🔒 " : ""}{lesson.id} · {lesson.duration}
        </span>
        {done && (
          <span data-testid={`lesson-done-${lesson.id}`} style={{ fontSize: 9, fontFamily: "monospace", letterSpacing: 1, color: "#22C55E", padding: "2px 6px", border: "1px solid #22C55E", borderRadius: 3 }}>
            ✓ PASSED
          </span>
        )}
      </div>
      <div style={{ color: locked ? THEME.mute : THEME.text, fontWeight: 700, fontSize: 14, lineHeight: 1.4, marginBottom: 4 }}>{lesson.title}</div>
      <div style={{ color: THEME.mute, fontSize: 11, fontFamily: "monospace", letterSpacing: 1 }}>{lesson.tag}</div>
    </button>
  );
}

function LessonViewer({ lesson, onClose, onQuiz }) {
  const s = lesson.axel_script || {};
  return (
    <Portal>
      <div data-testid="lesson-viewer-modal" style={modalBg}>
        <div style={modalCard}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2 }}>{lesson.id} · {lesson.tag} · {lesson.duration}</div>
              <h2 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 22, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>{lesson.title}</h2>
            </div>
            <button data-testid="lesson-viewer-close" onClick={onClose} style={ghostBtn}>Close</button>
          </div>
          {s.hook && <Block label="HOOK">{s.hook}</Block>}
          {s.core_teaching && <Block label="CORE TEACHING">{renderTeaching(s.core_teaching)}</Block>}
          {s.key_rules && <Block label="KEY RULES">{renderList(s.key_rules)}</Block>}
          {s.practice_assignment && <Block label="PRACTICE">{renderTeaching(s.practice_assignment)}</Block>}
          {Array.isArray(lesson.common_mistakes) && lesson.common_mistakes.length > 0 && (
            <Block label="COMMON MISTAKES">{renderList(lesson.common_mistakes)}</Block>
          )}
          {s.closing && <Block label="AXEL — CLOSING">{renderTeaching(s.closing)}</Block>}
          <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button data-testid="lesson-viewer-quiz-btn" onClick={onQuiz} style={primaryBtn}>
              Take the {Array.isArray(lesson.quiz) ? lesson.quiz.length : 10}-Question Quiz →
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function QuizModal({ lesson, onClose, onComplete }) {
  const questions = useMemo(() => lesson.quiz || [], [lesson.quiz]);
  const passThreshold = passThresholdForLesson(lesson);
  const storageKey = `flexx_quiz_inprogress_v1::${lesson.id}`;
  // Hydrate any in-progress quiz from localStorage so accidental refresh
  // doesn't wipe the trader's answers.
  const initial = useMemo(() => {
    try {
      const raw = typeof window !== "undefined" && window.localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          return {
            step: Math.min(Math.max(0, saved.step | 0), Math.max(0, questions.length - 1)),
            picks: saved.picks && typeof saved.picks === "object" ? saved.picks : {},
            revealed: saved.revealed && typeof saved.revealed === "object" ? saved.revealed : {},
            done: !!saved.done,
          };
        }
      }
    } catch { /* ignore corrupt cache */ }
    return { step: 0, picks: {}, revealed: {}, done: false };
  }, [storageKey, questions.length]);

  const [step, setStep] = useState(initial.step);
  const [picks, setPicks] = useState(initial.picks);
  const [revealed, setRevealed] = useState(initial.revealed);
  const [done, setDone] = useState(initial.done);
  const total = questions.length;
  const q = questions[step];
  const score = useMemo(() => {
    let c = 0;
    questions.forEach((qq) => { if (picks[qq.q_id] === qq.correct) c += 1; });
    return total ? c / total : 0;
  }, [picks, questions, total]);
  const correctCount = Math.round(score * total);

  // Persist to localStorage on every state change. Guard `done` so we
  // clear the cache once the trader submits.
  const completedRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (done || completedRef.current) {
        window.localStorage.removeItem(storageKey);
      } else {
        window.localStorage.setItem(storageKey, JSON.stringify({ step, picks, revealed, done }));
      }
    } catch { /* quota / disabled storage — non-fatal */ }
  }, [storageKey, step, picks, revealed, done]);

  const choose = (idx) => {
    if (revealed[q.q_id]) return;
    setPicks({ ...picks, [q.q_id]: idx });
    setRevealed({ ...revealed, [q.q_id]: true });
  };
  const next = () => {
    if (step < total - 1) setStep(step + 1);
    else { completedRef.current = true; setDone(true); onComplete?.(score, { correct: correctCount, total }); }
  };
  const retry = () => {
    setStep(0); setPicks({}); setRevealed({}); setDone(false); completedRef.current = false;
    if (typeof window !== "undefined") {
      try { window.localStorage.removeItem(storageKey); } catch { /* noop */ }
    }
  };

  return (
    <Portal>
      <div data-testid="quiz-modal" style={modalBg}>
        <div style={modalCard}>
          {!done ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2 }}>QUIZ · {lesson.id}</div>
                  <h3 style={{ margin: "4px 0 0", color: THEME.text, fontSize: 18 }}>{lesson.title}</h3>
                </div>
                <div style={{ fontFamily: "monospace", color: THEME.mute, fontSize: 11 }}>Q {step + 1} / {total}</div>
              </div>
              {/* Progress bar */}
              <div style={{ height: 4, background: THEME.border, borderRadius: 2, overflow: "hidden", marginBottom: 18 }}>
                <div style={{ height: "100%", width: `${((step + 1) / total) * 100}%`, background: THEME.gold, transition: "width 0.2s" }} />
              </div>
              {q && (
                <div data-testid={`quiz-q-${q.q_id}`}>
                  <div style={{ color: THEME.text, fontSize: 15, lineHeight: 1.6, marginBottom: 12 }}>{q.question}</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {q.options.map((opt, i) => {
                      const picked = picks[q.q_id] === i;
                      const correct = q.correct === i;
                      const reveal = !!revealed[q.q_id];
                      let bg = "transparent", bd = THEME.border, color = THEME.text;
                      if (reveal && correct) { bg = "#22C55E22"; bd = "#22C55E"; color = "#22C55E"; }
                      else if (reveal && picked && !correct) { bg = "#FF444422"; bd = "#FF4444"; color = "#FF4444"; }
                      else if (picked) { bg = `${THEME.gold}22`; bd = THEME.gold; color = THEME.gold; }
                      return (
                        <button key={i} type="button" data-testid={`quiz-q-${q.q_id}-opt-${i}`} onClick={() => choose(i)}
                          disabled={reveal}
                          style={{
                            all: "unset", cursor: reveal ? "default" : "pointer",
                            padding: "10px 14px", borderRadius: 8,
                            background: bg, border: `1px solid ${bd}`, color,
                            fontSize: 13, lineHeight: 1.5,
                          }}>{opt}</button>
                      );
                    })}
                  </div>
                  {revealed[q.q_id] && (
                    <div data-testid={`quiz-explanation-${q.q_id}`} style={{
                      marginTop: 12, padding: 12, borderRadius: 8,
                      background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}55`, borderLeft: `3px solid ${THEME.gold}`,
                      color: THEME.text, fontSize: 13, lineHeight: 1.6,
                    }}>
                      <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 2, marginBottom: 6 }}>
                        AXEL — {picks[q.q_id] === q.correct ? "✓ CORRECT" : "✗ NOT QUITE"}
                      </div>
                      {q.explanation}
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", gap: 10 }}>
                <button data-testid="quiz-close-btn" onClick={onClose} style={ghostBtn}>Exit</button>
                <button data-testid="quiz-next-btn" disabled={!revealed[q.q_id]} onClick={next}
                  style={{ ...primaryBtn, opacity: revealed[q.q_id] ? 1 : 0.4, cursor: revealed[q.q_id] ? "pointer" : "not-allowed" }}>
                  {step < total - 1 ? "Next →" : "Finish"}
                </button>
              </div>
            </>
          ) : (
            <div data-testid="quiz-result">
              <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>QUIZ COMPLETE</div>
              <h2 style={{ margin: "0 0 10px", color: THEME.text, fontSize: 26, fontFamily: "'Bebas Neue','Oswald',sans-serif" }}>
                {correctCount} / {total} ({Math.round(score * 100)}%)
              </h2>
              <div data-testid="quiz-pass-status" style={{
                padding: 12, borderRadius: 8, marginBottom: 14,
                background: score >= passThreshold ? "#22C55E15" : "#FF6B3515",
                border: `1px solid ${score >= passThreshold ? "#22C55E" : "#FF6B35"}`,
                color: score >= passThreshold ? "#22C55E" : "#FF6B35", fontSize: 13, lineHeight: 1.5,
              }}>
                {score >= passThreshold
                  ? `✅ PASSED — lesson logged. ${lesson.tier === 2 ? "+40 XP" : "+75 XP"} awarded.`
                  : `Below ${Math.round(passThreshold * 100)}% pass mark. AXEL says: re-read the lesson, then retry. No shortcuts.`}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                {score < passThreshold && (
                  <button data-testid="quiz-retry-btn" onClick={retry} style={primaryBtn}>Retry Quiz</button>
                )}
                <button data-testid="quiz-done-btn" onClick={onClose} style={ghostBtn}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// ── helpers ──────────────────────────────────────────────────
function Block({ label, children }) {
  return (
    <div style={{ marginTop: 14, padding: 14, background: "#0d1218", border: `1px solid ${THEME.border}`, borderRadius: 10 }}>
      <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>{label}</div>
      <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{children}</div>
    </div>
  );
}
function renderTeaching(v) {
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return renderList(v);
  if (v && typeof v === "object") {
    return Object.entries(v).map(([k, val]) => (
      <div key={k} style={{ marginBottom: 8 }}>
        <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, marginBottom: 4 }}>{k.toUpperCase().replace(/_/g, " ")}</div>
        <div>{renderTeaching(val)}</div>
      </div>
    ));
  }
  return String(v ?? "");
}
function renderList(arr) {
  if (!Array.isArray(arr)) return renderTeaching(arr);
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {arr.map((it, i) => <li key={i} style={{ marginBottom: 6 }}>{typeof it === "string" ? it : renderTeaching(it)}</li>)}
    </ul>
  );
}

const modalBg = {
  position: "fixed", inset: 0, zIndex: 11500,
  background: "rgba(0,0,0,0.86)", backdropFilter: "blur(6px)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
};
const modalCard = {
  width: "min(820px, 100%)", background: "#0C0C0C",
  border: `1px solid ${THEME.gold}55`, borderRadius: 14,
  boxShadow: `0 0 60px rgba(201,162,39,0.18)`,
  padding: 22, marginTop: 24, marginBottom: 24,
};
const primaryBtn = {
  padding: "10px 18px", borderRadius: 8,
  background: `linear-gradient(135deg, ${THEME.gold}, #E8B94F)`,
  color: "#0a0a0a", border: "none", cursor: "pointer",
  fontWeight: 800, fontFamily: "monospace", letterSpacing: 1.5, fontSize: 12, textTransform: "uppercase",
};
const ghostBtn = {
  padding: "10px 16px", borderRadius: 8,
  background: "transparent", border: `1px solid ${THEME.border}`,
  color: THEME.mute, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
};

// ── Tier 1 Graduation modal ─────────────────────────────────────
function GraduationModal({ onClose }) {
  return (
    <Portal>
      <div data-testid="tier1-graduation-modal" style={{ ...modalBg, alignItems: "center" }}>
        <Confetti />
        <div style={{
          ...modalCard, width: "min(640px, 100%)",
          textAlign: "center", borderColor: THEME.gold,
          boxShadow: `0 0 80px rgba(201,162,39,0.5)`,
          marginTop: 0, marginBottom: 0,
        }}>
          <div style={{ fontSize: 60, lineHeight: 1, marginBottom: 6 }}>🎓</div>
          <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 11, letterSpacing: 3, marginBottom: 6 }}>FLEXX ACADEMY</div>
          <h2 style={{ margin: "0 0 14px", color: THEME.text, fontSize: 32, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>
            TIER 1 GRADUATE
          </h2>
          <div style={{
            margin: "0 auto 18px", maxWidth: 520, padding: 16,
            background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}55`, borderLeft: `3px solid ${THEME.gold}`,
            borderRadius: 10, color: THEME.text, fontSize: 14, lineHeight: 1.7, fontStyle: "italic", textAlign: "left",
          }}>
            <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 2, marginBottom: 8, fontStyle: "normal" }}>AXEL — GRADUATION</div>
            "That's Tier 1 done. You now know what most traders never bother to learn. But knowing isn't trading. Tier 2 is where you build your edge. See you there."
          </div>
          <div style={{ color: THEME.mute, fontSize: 12, marginBottom: 18, fontFamily: "monospace", letterSpacing: 1 }}>
            🏆 Badge unlocked: <b style={{ color: THEME.gold }}>FLEXX Tier 1 Graduate</b>
          </div>
          <button data-testid="tier1-graduation-close" onClick={onClose}
            style={{ ...primaryBtn, padding: "12px 28px", fontSize: 13 }}>
            Continue → Tier 2
          </button>
        </div>
      </div>
    </Portal>
  );
}

// Pure-CSS confetti — spawns gold + accent particles for ~5s.
function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 80 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.8,
    duration: 3.5 + Math.random() * 2.2,
    size: 6 + Math.random() * 8,
    rot: Math.random() * 360,
    color: ["#C9A227", "#E8B94F", "#FFFFFF", "#22C55E"][i % 4],
  })), []);
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 12000 }}>
      <style>{`
        @keyframes flexx-confetti-fall {
          0%   { transform: translate3d(0,-10vh,0) rotate(0); opacity: 1; }
          100% { transform: translate3d(20px,110vh,0) rotate(720deg); opacity: 0.85; }
        }
      `}</style>
      {pieces.map((p) => (
        <span key={p.id} style={{
          position: "absolute", top: 0, left: `${p.left}%`,
          width: p.size, height: p.size * 0.4,
          background: p.color, borderRadius: 1,
          transform: `rotate(${p.rot}deg)`,
          animation: `flexx-confetti-fall ${p.duration}s ${p.delay}s linear forwards`,
          boxShadow: `0 0 6px ${p.color}88`,
        }} />
      ))}
    </div>
  );
}
